/**
 * PWA Install Tutorial
 *
 * Platform-specific installation instructions.
 * Detects iOS/Android/Desktop at runtime via StepFactory.
 */

import type { TutorialDefinition, TutorialStep } from './types'

function createSteps(): TutorialStep[] {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window)
  const isAndroid = /Android/.test(navigator.userAgent)

  if (isIOS) {
    return [
      {
        popover: {
          title: '📱 App installieren',
          description:
            'So installierst du die TuS Trainer-App auf deinem iPhone oder iPad — in nur 3 Schritten.',
        },
      },
      {
        popover: {
          title: 'Schritt 1: Teilen-Symbol',
          description:
            'Tippe auf das <strong>Teilen-Symbol</strong> <svg class="tutorial-share-icon" style="display:inline-block;vertical-align:middle;width:1.2em;height:1.2em" viewBox="0 0 24 24" fill="currentColor"><path d="M16 5l-1.42 1.42-1.59-1.59V16h-1.98V4.83L9.42 6.42 8 5l4-4 4 4zm4 5v11c0 1.1-.9 2-2 2H6c-1.1 0-2-.9-2-2V10c0-1.1.9-2 2-2h3v2H6v11h12V10h-3V8h3c1.1 0 2 .9 2 2z"/></svg> in der Safari-Leiste am unteren Bildschirmrand.',
        },
      },
      {
        popover: {
          title: 'Schritt 2: Zum Home-Bildschirm',
          description:
            'Scrolle im Teilen-Menü nach unten und tippe auf <strong>„Zum Home-Bildschirm"</strong>.',
        },
      },
      {
        popover: {
          title: 'Schritt 3: Hinzufügen',
          description:
            'Tippe oben rechts auf <strong>„Hinzufügen"</strong>. Die App erscheint als Icon auf deinem Home-Bildschirm.',
        },
      },
      {
        popover: {
          title: '✅ Fertig!',
          description:
            'Öffne die App über das neue Icon auf deinem Home-Bildschirm. Sie funktioniert dann wie eine normale App — auch offline!',
        },
      },
    ]
  }

  if (isAndroid) {
    return [
      {
        popover: {
          title: '📱 App installieren',
          description:
            'So installierst du die TuS Trainer-App auf deinem Android-Gerät — in nur 3 Schritten.',
        },
      },
      {
        popover: {
          title: 'Schritt 1: Menü öffnen',
          description: 'Tippe auf das <strong>Drei-Punkte-Menü ⋮</strong> oben rechts in Chrome.',
        },
      },
      {
        popover: {
          title: 'Schritt 2: App installieren',
          description:
            'Tippe auf <strong>„App installieren"</strong> oder <strong>„Zum Startbildschirm hinzufügen"</strong>.',
        },
      },
      {
        popover: {
          title: 'Schritt 3: Bestätigen',
          description: 'Bestätige mit <strong>„Installieren"</strong>.',
        },
      },
      {
        popover: {
          title: '✅ Fertig!',
          description:
            'Die App erscheint als Icon auf deinem Startbildschirm. Sie funktioniert dann wie eine normale App — auch offline!',
        },
      },
    ]
  }

  // Desktop fallback
  return [
    {
      popover: {
        title: '💻 App installieren (Desktop)',
        description:
          'Klicke auf das <strong>Install-Icon</strong> in der Adressleiste deines Browsers (Chrome/Edge), um die App auf deinem Desktop zu installieren.',
      },
    },
  ]
}

export const pwaInstallTutorial: TutorialDefinition = {
  id: 'pwa-install',
  title: 'App installieren',
  description: 'Erfahre, wie du die Trainer-App auf deinem Gerät installierst.',
  icon: 'share',
  steps: createSteps,
}
